import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUserRole } from '@/hooks/useUserRole'
import { useAppSelector } from '@/app/hooks'
import { selectIsAuthenticated } from '@/features/auth/authSlice'
import { showValidationMessage } from '@/lib/utils/notifications'
import { isReimbursementsAccessDeniedRole } from '@/features/reimbursements/utils/reimbursementsAccess'
import type { ReimbursementsAccessGuardProps } from './ReimbursementsAccessGuard.types'

/**
 * Reimbursements list/detail gate.
 *
 * Angular: opening `/defaults` without access shows Validation Message "Access Denied"
 * and returns to dashboard (AuthGuard / API interceptor). EXTERNAL_INSURANCE and
 * ROLE_LIBERTY_FIN_MGR are blocked from this module — they must not open list or detail.
 */
export function ReimbursementsAccessGuard({
  children,
  requiredPolicies,
  redirectTo = '/dashboard',
}: ReimbursementsAccessGuardProps) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const user = useAppSelector((state) => state.auth.user)
  const { hasPolicy, roleName } = useUserRole()
  const navigate = useNavigate()
  const deniedPromptedRef = useRef(false)

  const policiesReady = Boolean(isAuthenticated && user?.current_role)
  const isDeniedRole = isReimbursementsAccessDeniedRole(roleName)
  const hasRequiredPolicy = hasPolicy(requiredPolicies)
  const allowed = policiesReady && !isDeniedRole && hasRequiredPolicy

  useEffect(() => {
    if (!policiesReady || allowed || deniedPromptedRef.current) return
    deniedPromptedRef.current = true
    showValidationMessage('Access Denied', 'Validation Message')
    navigate(redirectTo, { replace: true })
  }, [allowed, navigate, policiesReady, redirectTo])

  if (!isAuthenticated) {
    return <>{children}</>
  }

  if (!user || !user.current_role) {
    return <>{children}</>
  }

  if (isDeniedRole || !hasRequiredPolicy) {
    return null
  }

  return <>{children}</>
}
